import type { ComponentProps } from 'react';
import { ChevronLeft, Search } from 'lucide-react';
import { CommandEmpty, CommandList } from '@/components/ui/command';
import { Loader } from '@/components/ui/loader';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ActionsPage } from './pages/actions-page';
import { RootPage } from './pages/root-page';
import { ScopePage } from './pages/scope-page';
import { ProductFolderPicker } from './pickers/product-folder-picker';
import { ProjectPicker } from './pickers/project-picker';
import { StaticPicker } from './pickers/static-picker';
import { CommandRows } from './sections';
import { OpenTabRows } from './open-tab-rows';
import { pageKey, type MenuPage } from './types';
import type { GlobalCommandMenuCopy } from './global-command-menu-copy';

interface GlobalCommandMenuContentProps {
  page: MenuPage;
  stackDepth: number;
  text: GlobalCommandMenuCopy;
  breadcrumbLabel: string | null;
  showEmptyState: boolean;
  /** Results are still on their way; shown instead of "No results". */
  showLoadingState: boolean;
  openTabs: ComponentProps<typeof OpenTabRows>;
  pageActions: ComponentProps<typeof CommandRows>;
  rootPage: ComponentProps<typeof RootPage>;
  scopePage: ComponentProps<typeof ScopePage>;
  actionsPage: ComponentProps<typeof ActionsPage>;
  term: string;
  orgSlug?: string;
  close: () => void;
  popPage: () => void;
}

export function GlobalCommandMenuContent({
  page,
  stackDepth,
  text,
  breadcrumbLabel,
  showEmptyState,
  showLoadingState,
  openTabs,
  pageActions,
  rootPage,
  scopePage,
  actionsPage,
  term,
  orgSlug,
  close,
  popPage,
}: GlobalCommandMenuContentProps) {
  return (
    <CommandList className="max-h-none min-h-0 overflow-hidden">
      <ScrollArea
        // Remount per page so a sub-page opens at the top, not at the root
        // list's scroll offset (e.g. Change language picked near the end).
        key={pageKey(page)}
        className="w-full max-w-full"
        maskHeight={16}
        maskClassName="before:from-(--chat-composer) after:from-(--chat-composer)"
        // Radix wraps content in a `display: table` div that grows to the widest
        // row; force block so long titles truncate instead of overflowing.
        viewportClassName="max-h-[min(440px,56dvh)] scroll-py-2 [&>div]:!block"
      >
        {stackDepth > 0 ? (
          <div className="px-2 pt-2">
            <button
              type="button"
              onClick={popPage}
              className="text-muted-foreground hover:text-foreground hover:bg-accent/60 inline-flex h-8 items-center gap-1.5 rounded-[12px] px-2.5 text-xs font-medium transition-colors duration-100"
            >
              <ChevronLeft className="size-3.5" />
              <span>{text.backToAll}</span>
              {breadcrumbLabel ? (
                <span className="text-muted-foreground/60 ml-0.5">
                  / {breadcrumbLabel}
                </span>
              ) : null}
            </button>
          </div>
        ) : null}

        {showLoadingState ? (
          <div
            className="flex justify-center py-6"
            data-command-menu-loading
            aria-busy="true"
          >
            <Loader aria-label={text.searching} />
          </div>
        ) : null}

        {showEmptyState ? (
          <CommandEmpty>
            <div className="flex flex-col items-center gap-2 py-8">
              <Search className="text-muted-foreground/50 size-5" />
              <p className="text-muted-foreground text-sm">
                {text.noResultsFound}
              </p>
            </div>
          </CommandEmpty>
        ) : null}

        <div key={pageKey(page)} className="py-2">
          {page.kind === 'root' ? <OpenTabRows {...openTabs} /> : null}
          {(page.kind === 'root' || page.kind === 'actions') &&
          pageActions.commands.length > 0 ? (
            <CommandRows {...pageActions} />
          ) : null}
          {page.kind === 'root' ? <RootPage {...rootPage} /> : null}
          {page.kind === 'scope' ? (
            <ScopePage {...scopePage} scope={page.scope} />
          ) : null}
          {page.kind === 'actions' ? <ActionsPage {...actionsPage} /> : null}
          {page.kind === 'picker' && page.subPage.type === 'project-picker' ? (
            <ProjectPicker
              term={term}
              heading={page.label}
              noneLabel={page.subPage.noneLabel}
              emptyLabel={text.noResultsFound}
              orgSlug={orgSlug}
              onPick={page.subPage.onPick}
              onDone={close}
            />
          ) : null}
          {page.kind === 'picker' &&
          page.subPage.type === 'product-folder-picker' ? (
            <ProductFolderPicker
              term={term}
              heading={page.label}
              noneLabel={page.subPage.noneLabel}
              emptyLabel={text.noResultsFound}
              orgSlug={orgSlug}
              onPick={page.subPage.onPick}
              onDone={close}
            />
          ) : null}
          {page.kind === 'picker' && page.subPage.type === 'static' ? (
            <StaticPicker
              term={term}
              heading={page.label}
              items={page.subPage.items}
              emptyLabel={text.noResultsFound}
              onDone={close}
            />
          ) : null}
        </div>
      </ScrollArea>
    </CommandList>
  );
}
